import { useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, LinearProgress, MenuItem,
  Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { getReports, getReport, reviewReport } from "services/reports";
import { STATUS_LABELS, REASON_LABELS, ACTION_LABELS, isOpen, formatDate, deadline, canTakeAction } from "./reportUtils";

const selectProps = { IconComponent: ArrowDropDownIcon, sx: { "& .MuiSelect-icon": { display: "block", right: 8 } } };
const filterFieldSx = {
  minWidth: 190,
  "& .MuiOutlinedInput-root": { minHeight: 40 },
  "& .MuiSelect-select": { padding: "8px 32px 8px 12px !important" },
};
const filterLabelProps = {
  shrink: true,
  sx: { "&.MuiInputLabel-shrink": { transform: "translate(14px, -9px) scale(0.75)" } },
};
const colors = { pending: "warning", under_review: "info", resolved: "success", dismissed: "default" };

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState("");
  const [overdue, setOverdue] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reportId, setReportId] = useState(null);
  const [report, setReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailRefresh, setDetailRefresh] = useState(0);
  const [action, setAction] = useState("");
  const [note, setNote] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [now, setNow] = useState(Date.now());
  const saveInProgress = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => { mounted.current = false; clearInterval(timer); };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError("");
    getReports({ page: page + 1, limit, ...(status ? { status } : {}),
      ...(overdue ? { overdue: overdue === "true" } : {}) }, controller.signal)
      .then(data => {
        if (controller.signal.aborted) return;
        // A review can remove the final row from the current filtered page.
        if (page > 0 && page * limit >= data.total) {
          setPage(Math.max(0, Math.ceil(data.total / limit) - 1)); return;
        }
        setReports(data.reports || []); setTotal(data.total || 0);
      })
      .catch(err => { if (!controller.signal.aborted) { setReports([]); setTotal(0); setError(err.message || "Unable to load reports."); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [page, limit, status, overdue, refresh]);

  useEffect(() => {
    if (!reportId) return undefined;
    const controller = new AbortController();
    setDetailLoading(true); setDetailError(""); setReport(null);
    setAction(""); setNote(""); setConfirm(false);
    getReport(reportId, controller.signal)
      .then(data => { if (!controller.signal.aborted) setReport(data); })
      .catch(err => { if (!controller.signal.aborted) setDetailError(err.message || "Unable to load report."); })
      .finally(() => { if (!controller.signal.aborted) setDetailLoading(false); });
    return () => controller.abort();
  }, [reportId, detailRefresh]);

  const close = () => { if (!saveInProgress.current) { setReportId(null); setReport(null); setConfirm(false); } };
  const changeFilter = setter => event => { setter(event.target.value); setPage(0); };
  const save = async (payload) => {
    if (saveInProgress.current) return;
    saveInProgress.current = true; setSaving(true); setDetailError("");
    try {
      const updated = await reviewReport(report.id, payload);
      if (!mounted.current) return;
      // Review response omits associations. Retain detail data until the next fetch.
      setReport(current => ({ ...current, ...updated }));
      setRefresh(value => value + 1); setDetailRefresh(value => value + 1); setConfirm(false); setAction(""); setNote("");
      setSuccess(payload.status === "under_review" ? "Report marked under review." : "Report review completed.");
    } catch (err) {
      if (!mounted.current) return;
      setConfirm(false);
      setDetailError(err.message || "Unable to update the report. Refresh its details before trying again.");
    } finally {
      saveInProgress.current = false;
      if (mounted.current) setSaving(false);
    }
  };
  const finishReview = () => save({ status: action === "none" ? "dismissed" : "resolved", action, resolutionNote: note.trim() });
  const reviewValid = Boolean(action && note.trim() && note.trim().length <= 2000 && canTakeAction(report, action));
  const time = report ? deadline(report, now) : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
            <MDTypography variant="h6" color="white">Listing Reports</MDTypography>
          </MDBox>
          <Box p={3}>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Review each reported listing and its seller within 24 hours of submission.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
              <TextField select size="small" label="Status" value={status} onChange={changeFilter(setStatus)} SelectProps={selectProps}
                InputLabelProps={filterLabelProps} sx={filterFieldSx}>
                <MenuItem value="">All statuses</MenuItem>
                {Object.entries(STATUS_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Deadline" value={overdue} onChange={changeFilter(setOverdue)} SelectProps={selectProps}
                InputLabelProps={filterLabelProps} sx={filterFieldSx}>
                <MenuItem value="">All deadlines</MenuItem>
                <MenuItem value="true">Overdue only</MenuItem>
                <MenuItem value="false">Not overdue</MenuItem>
              </TextField>
              <Button disabled={loading} onClick={() => setRefresh(value => value + 1)}>Refresh reports</Button>
            </Stack>
            {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => setRefresh(value => value + 1)}>Retry</Button>}>{error}</Alert>}
            <TableContainer aria-busy={loading}>
              <Table size="small" aria-label="Listing reports">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>{["Report", "Listing / Seller", "Reason", "Submitted", "Deadline", "Status", "Actions"].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow>
                </TableHead>
                <TableBody>
                  {!loading && !error && reports.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}>No reports match these filters.</TableCell></TableRow>}
                  {reports.map(item => {
                    const due = deadline(item, now);
                    return <TableRow key={item.id} hover>
                      <TableCell>#{item.id}</TableCell>
                      <TableCell sx={{ maxWidth: 240, overflowWrap: "anywhere" }}>
                        <Typography variant="body2">{item.listingTitle}</Typography>
                        <Typography variant="caption" color="text.secondary">Seller {item.reportedUserId ? `#${item.reportedUserId}` : "deleted"}</Typography>
                      </TableCell>
                      <TableCell>{REASON_LABELS[item.reason] || item.reason}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(item.createdAt)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color={due.overdue ? "error.main" : "text.primary"}>{due.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDate(item.dueAt)}</Typography>
                      </TableCell>
                      <TableCell><Chip size="small" label={STATUS_LABELS[item.status] || item.status} color={colors[item.status] || "default"} /></TableCell>
                      <TableCell><Button disabled={loading} size="small" aria-label={`Review report ${item.id}`} onClick={() => setReportId(item.id)}>View report</Button></TableCell>
                    </TableRow>;
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={total} page={page} rowsPerPage={limit} rowsPerPageOptions={[10, 20, 50]}
              onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={event => { setLimit(Number(event.target.value)); setPage(0); }}
              backIconButtonProps={{ disabled: loading || page === 0 }} nextIconButtonProps={{ disabled: loading || (page + 1) * limit >= total }} />
          </Box>
        </Card>
      </MDBox>
      <Dialog open={reportId !== null} onClose={close} maxWidth="md" fullWidth aria-labelledby="report-title">
        <DialogTitle id="report-title">
          Report #{reportId}
          <IconButton aria-label="Close report" disabled={saving} onClick={close} sx={{ position: "absolute", right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ color: "grey.800" }}>
          {detailLoading && <Box p={4} textAlign="center"><CircularProgress aria-label="Loading report details" /></Box>}
          {detailError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" disabled={saving} onClick={() => setDetailRefresh(value => value + 1)}>Reload details</Button>}>{detailError}</Alert>}
          {report && !detailLoading && <>
            <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
              <Chip size="small" label={STATUS_LABELS[report.status] || report.status} color={colors[report.status] || "default"} />
              <Chip size="small" label={time.label} color={time.overdue ? "error" : "default"} />
            </Stack>
            <Typography variant="body2" mb={2}>This report covers the listing and its seller.</Typography>
            <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "150px 1fr" }, gap: 1,
              "& dt": { fontWeight: 600 }, "& dd": { m: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere" } }}>
              {[
                ["Listing", `${report.listingTitle}${report.listing ? ` (#${report.listingId}, ${report.listing.status})` : " (deleted)"}`],
                ["Reported seller", report.reportedUser ? `${report.reportedUser.fullName} (#${report.reportedUser.id}) — ${report.reportedUser.email} — ${report.reportedUser.isActive ? "Active" : "Suspended / inactive"}` : "Deleted user"],
                ["Reported by", report.reporter ? `${report.reporter.fullName} (#${report.reporter.id}) — ${report.reporter.email}` : "Deleted user"],
                ["Reason", REASON_LABELS[report.reason] || report.reason], ["Details", report.details || "No additional details provided."],
                ["Submitted", formatDate(report.createdAt)], ["Review deadline", formatDate(report.dueAt)],
                ["Reviewer", report.reviewer?.fullName || (report.reviewedBy ? `Admin #${report.reviewedBy}` : "Not assigned")],
                ...(!isOpen(report) ? [["Completed", formatDate(report.resolvedAt)], ["Action taken", ACTION_LABELS[report.action] || report.action || "—"], ["Resolution note", report.resolutionNote || "—"]] : []),
              ].map(([label, value]) => <Box key={label} sx={{ display: "contents" }}><Typography component="dt" variant="body2">{label}</Typography><Typography component="dd" variant="body2">{value}</Typography></Box>)}
            </Box>
            {isOpen(report) && <>
              <Divider sx={{ my: 3 }} />
              {report.status === "pending" && <Button disabled={saving || Boolean(detailError)} variant="outlined" onClick={() => save({ status: "under_review" })}
                sx={{ mb: 3, color: "info.main", borderColor: "info.main" }}>Mark under review</Button>}
              <Typography variant="h6" mb={2}>Complete review</Typography>
              {(!report.listing || !report.reportedUser) && <Alert severity="info" sx={{ mb: 2 }}>A linked record has been deleted. Choose an available action or dismiss with an explanation.</Alert>}
              <TextField select fullWidth label="Review action" value={action} disabled={saving} onChange={event => setAction(event.target.value)} SelectProps={selectProps}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: "grey.700",
                    "&.MuiInputLabel-shrink": { transform: "translate(14px, -9px) scale(0.75)" },
                  },
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": { minHeight: 40 },
                  "& .MuiSelect-select": { padding: "8px 32px 8px 12px !important" },
                }}>
                <MenuItem value="" disabled>Select an action</MenuItem>
                {Object.entries(ACTION_LABELS).map(([value, label]) => <MenuItem key={value} value={value} disabled={!canTakeAction(report, value)}>{value === "none" ? "Dismiss — no action" : label}</MenuItem>)}
              </TextField>
              {action && action !== "none" && <Alert severity="warning" sx={{ mb: 2 }}>{action.includes("suspend_seller") ? "Suspending the seller blocks account access and takes all their active and pending listings offline." : "The reported listing will be removed from the marketplace."}{action === "remove_listing_and_suspend_seller" && " The reported listing will also be flagged and remain removed after account reactivation."}</Alert>}
              <TextField fullWidth required multiline minRows={3} label="Resolution note" value={note} disabled={saving} onChange={event => setNote(event.target.value)}
                InputLabelProps={{ sx: { color: "grey.700" } }} inputProps={{ maxLength: 2000 }}
                helperText={`${note.length}/2000 characters. Internal note; not shown to the reporter.`} />
              <Button variant="contained" color={action === "none" ? "primary" : "error"} sx={{ mt: 2, color: "white" }} disabled={!reviewValid || saving || Boolean(detailError)} onClick={() => setConfirm(true)}>Complete review</Button>
            </>}
          </>}
        </DialogContent>
        <DialogActions><Button disabled={saving} onClick={close}>Close</Button></DialogActions>
      </Dialog>
      <Dialog open={confirm} onClose={() => { if (!saving) setConfirm(false); }} aria-labelledby="confirm-review-title" maxWidth="xs" fullWidth>
        <DialogTitle id="confirm-review-title">Confirm report review</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{action === "none" ? "Dismiss this report without moderation action?" : `${ACTION_LABELS[action]} for report #${reportId}?`}</Typography>
          <Typography variant="body2" mt={2}>The report will be closed and the reporter notified. Closed reports cannot be edited or reopened.</Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setConfirm(false)}>Cancel</Button>
          <Button variant="contained" color={action === "none" ? "primary" : "error"} sx={{ color: "white" }} disabled={saving || !reviewValid} onClick={finishReview}>{saving ? "Saving…" : "Confirm review"}</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(success)} autoHideDuration={5000} onClose={() => setSuccess("")}><Alert severity="success" onClose={() => setSuccess("")}>{success}</Alert></Snackbar>
    </DashboardLayout>
  );
}
